import * as React from "react";

import * as subscriptions from "@client/features/subscriptions";

import * as constants from "@client/constants";
import * as typography from "@client/shared/typography";
import * as utils from "@client/utils";

import {Button, ButtonGroup} from "@client/components/Button";
import {Prose} from "@client/components/Prose";
import {useAppSelector} from "@client/store";
import {models, reverse} from "@reactivated";

interface Props {
    subscribe: () => void;
    unsubscribe: () => void;
    showPayPal: () => void;
    subscription: models["core.payments.Subscription"] | null;
    canUsePayPal: boolean;
    isShowingPayPal: boolean;
    subscriptionDollarsPerMonth: number;
    platform: constants.Platform;
}

const AppleSubscriptionDetail = (props: Props) => {
    const {isOnline, isLoaded} = useAppSelector((state) => ({
        isOnline: state.scenes.isOnline,
        isLoaded: state.subscriptions.isLoaded,
    }));
    const PRODUCT_NAME = useAppSelector((state) =>
        subscriptions.getSubscriptionProductName(state, "basic"),
    );

    const [storeState, setStoreState] = React.useState(() => ({
        product: CdvPurchase.store.get(PRODUCT_NAME),
    }));

    React.useEffect(() => {
        const updateProduct = () => {
            setStoreState({product: CdvPurchase.store.get(PRODUCT_NAME)});
        };
        CdvPurchase.store.when().verified(updateProduct);

        return () => {
            CdvPurchase.store.off(updateProduct);
        };
    });

    const purchaseSubscription = () => {
        void storeState.product?.getOffer()?.order();
    };

    const {product} = storeState;

    if (product == null || isOnline === false || isLoaded === false) {
        return (
            <p>
                Cannot load subscriptions at this time. Make sure you are online. You
                can also try restarting the app.
            </p>
        );
    }

    const common = (
        <>
            <p>
                View our{" "}
                <typography.A href={reverse("privacy_policy")}>
                    Privacy Policy
                </typography.A>{" "}
                and{" "}
                <typography.A href={reverse("terms_of_service")}>
                    Terms of Service
                </typography.A>
            </p>
        </>
    );

    if (product.owned === true) {
        // Fetch subscription could come back as type: none when we first subscribe.
        // So we guard against it.
        if (props.subscription == null || props.subscription.type === "none") {
            return <div>Loading subscription information</div>;
        }
        if (props.subscription.type === "stripe") {
            throw new Error("Impossible");
        }

        return (
            <Prose>
                <p>
                    You are currently on a {product.title} paid until{" "}
                    {utils.formatDate(props.subscription.expires_date)}
                </p>
                <p>You’ll be billed every month until you cancel.</p>
                <p>Thank you for being a loyal customer.</p>
                {common}
                <p>
                    <Button onClick={() => CdvPurchase.store.manageSubscriptions()}>
                        Manage Subscription
                    </Button>
                </p>
            </Prose>
        );
    }

    return (
        <Prose>
            <p>
                To continue using Joy after {constants.TRIAL_DAYS} days, you need a
                subscription. It’s just {product.pricing?.price} per month.
            </p>
            <p>Paid subscriptions keep Joy ad-free.</p>
            {common}
            <ButtonGroup>
                <Button onClick={purchaseSubscription}>Purchase {product.title}</Button>
                <Button onClick={() => CdvPurchase.store.restorePurchases()}>
                    Restore Purchases
                </Button>
            </ButtonGroup>
        </Prose>
    );
};

export const SubscriptionDetail = (props: Props) => {
    if (
        props.subscription &&
        props.subscription.type === "stripe" &&
        props.subscription.status === "active"
    ) {
        return (
            <Prose>
                <p>
                    You are currently subscribed. Thank you for being a loyal customer.
                </p>
                <p>
                    Your subscription is currently paid until{" "}
                    {utils.formatDate(props.subscription.current_period_end)}.
                </p>
                <p>On that date, you’ll be billed again.</p>
                <Button href={reverse("billing")} track="billing">
                    Update Billing Information
                </Button>
                <Button onClick={props.unsubscribe}>Unsubscribe</Button>
            </Prose>
        );
    } else if (
        props.subscription &&
        props.subscription.type === "stripe" &&
        props.subscription.status === "past_due"
    ) {
        return (
            <Prose>
                <p>
                    Looks like we had trouble collecting payment. To ensure
                    uninterrupted service, please update your payment details.
                </p>
                <Button onClick={() => props.subscribe()}>Update Payment Info</Button>
            </Prose>
        );
    } else if (
        props.subscription &&
        props.subscription.type === "apple" &&
        props.platform !== "ios"
    ) {
        return (
            <Prose>
                <p>
                    You are currently on an Apple subscription paid until{" "}
                    {utils.formatDate(props.subscription.expires_date)}
                </p>
                <p>You’ll be billed every month until you cancel.</p>
                <p>Thank you for being a loyal customer.</p>
                <Button href="https://support.apple.com/en-us/HT202039">
                    Manage Subscription
                </Button>
            </Prose>
        );
    } else {
        if (props.platform === "ios") {
            return <AppleSubscriptionDetail {...props} />;
        }

        return (
            <Prose>
                <p>
                    To continue using Joy after {constants.TRIAL_DAYS} days, you need a
                    subscription. It’s just {props.subscriptionDollarsPerMonth} bucks a
                    month.
                </p>
                <p>Paid subscriptions help keep Joy ad-free.</p>
                <Button onClick={() => props.subscribe()}>Subscribe</Button>
                {!props.isShowingPayPal && (
                    <Button onClick={props.showPayPal}>
                        I don’t have a credit card or a debit card.
                    </Button>
                )}
                {props.isShowingPayPal &&
                    props.canUsePayPal && [
                        <p key={1}>
                            You can use PayPal to pay yearly by clicking below.
                        </p>,
                        <Button key={2} href="/paypal/">
                            PayPal Checkout
                        </Button>,
                    ]}
                {props.isShowingPayPal && !props.canUsePayPal && (
                    <p>
                        If you want to use PayPal or pay yearly, visit the web app using
                        your browser.
                    </p>
                )}
            </Prose>
        );
    }
};
